import { Dispatch } from 'redux';

import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import { addDocument } from 'modules/documents/redux/documentActions';
import { getDocumentReplacedWithTags } from 'modules/documents/api/replaceDocumentTags';
import { getLinkedTo } from 'modules/documents/components/Modals/UploadDocumentModal/utils/getLinkedTo';
import { getIncentivesMarketPurchaseContractTemplateId } from 'modules/incentivesMarket/constants/incentivesMarketSocietyTemplates';
import { IncentiveOfferOperation } from 'modules/incentivesMarket/types';

import { convertHtmlToPdfBlob } from './convertHtmlToPdfBlob';
import getOperationReference from './getOperationReference';

type SocietyWithTemplate = Parameters<
  typeof getIncentivesMarketPurchaseContractTemplateId
>[0];

export type GenerateIncentivesMarketPurchaseContractParams = {
  dispatch: Dispatch;
  operation: IncentiveOfferOperation;
  societyId: string;
  society: SocietyWithTemplate;
  userId?: string;
  t: (key: string) => string;
};

export type GenerateIncentivesMarketPurchaseContractResult = {
  documentId: string;
  operationReference: string;
};

export const generateIncentivesMarketPurchaseContract = async ({
  dispatch,
  operation,
  societyId,
  society,
  userId,
  t,
}: GenerateIncentivesMarketPurchaseContractParams): Promise<GenerateIncentivesMarketPurchaseContractResult> => {
  const templateId = getIncentivesMarketPurchaseContractTemplateId(society);

  if (!templateId) {
    throw new Error('NO_INCENTIVES_MARKET_TEMPLATE');
  }

  const documentUpdated = await (dispatch as any)(
    getDocumentReplacedWithTags({
      documentId: templateId,
      sources: {
        society: societyId,
        incentiveOfferOperation: operation._id,
      },
      language: 'es',
    })
  );

  if (!documentUpdated?.html) {
    throw new Error('NO_INCENTIVES_MARKET_TEMPLATE');
  }

  const operationReference = getOperationReference(operation);

  const pdfOptions = {
    filename: `${t('IncentivesMarketPurchaseContract')}.pdf`,
    margin: [10, 10, 10, 10],
    pagebreak: { mode: 'avoid-all' },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
  };

  const fileBlob = await convertHtmlToPdfBlob(documentUpdated.html, pdfOptions);

  const contractFile = new File(
    [fileBlob],
    `${t('IncentivesMarketPurchaseContract')}.pdf`,
    { type: 'application/pdf' }
  );

  const linkedTo = getLinkedTo({
    incentiveOfferOperationId: operation._id,
  });

  const newDocumentData = {
    file: contractFile,
    name: `${t('IncentivesMarketPurchaseContract')} ${operationReference}`,
    date: new Date(),
    size: contractFile.size,
    author: userId,
    society: societyId,
    fileType: fileTypes.PDF,
    category: documentTypes.DOCUMENTS,
    subcategory: 'INCENTIVES_MARKET_UNIT_PURCHASE',
    description: t('IncentivesMarketPurchaseContractDescription'),
    linkedTo,
  };

  const createdDoc = await (dispatch as any)(addDocument(newDocumentData));

  if (!createdDoc?._id) {
    throw new Error('CONTRACT_UPLOAD_FAILED');
  }

  return {
    documentId: createdDoc._id,
    operationReference,
  };
};
